import { observer } from "mobx-react";
import * as React from "react";
import Localization from "../../core/Localization";
import MenuItem from "../../coreui/MenuItem";
import PaneRow from "../../models/PaneRow";
import { AccessLevel } from "../../mustangui/Api";
import PaneDataStore from "../../stores/PaneDataStore";
import { ActionButtonProps, ActionButtonRuntimeProps } from "../ActionButton";
import ApiButton from "../ApiButton";
import { MenuItemProps } from "../MenuItem";
import { SelectChildProps } from "../SelectControl";

interface Props extends ActionButtonProps {}

@observer
export class SelectButton extends React.Component<Props> {
  private static onClick(propagated: SelectChildProps): void {
    propagated.parentSelect!.search(null);
  }

  public static renderMenuItem(props: MenuItemProps): JSX.Element {
    const { config, runtime, ...otherProps } = props;
    const configProps = (config as unknown) as Props;
    const runtimeProps = runtime as ActionButtonRuntimeProps;

    function onClick(): void {
      if (runtimeProps.accessLevel >= AccessLevel.actionable) {
        SelectButton.onClick(configProps.propagated);
        configProps.propagated.onItemClicked!();
      }
    }

    return (
      <MenuItem
        disabled={runtimeProps.accessLevel === AccessLevel.disabled}
        iconName={configProps.iconName}
        indent={props.config.propagated ? props.config.propagated.indent : 0}
        onClick={onClick}
        {...otherProps}
      >
        {runtimeProps.label}
      </MenuItem>
    );
  }

  private onClick = (): void => {
    SelectButton.onClick(this.props.propagated);
  };

  public render(): React.ReactNode {
    const runtimeProperties = PaneRow.getWidgetProperties(
      this.props.dataId,
      this.props.name
    ) as ActionButtonRuntimeProps;

    if (runtimeProperties.accessLevel === undefined) {
      return null;
    }

    if (runtimeProperties.accessLevel === AccessLevel.hidden) {
      return null;
    }

    const isIconOnly = !runtimeProperties.label;
    const label = isIconOnly
      ? Localization.getBuiltInMessage("SelectControl.selectButtonLabel")
      : runtimeProperties.label;

    // Find how many rows are in the select grid and report it out for users
    // needing accessible descriptions.
    let alternateText: string = "";
    if (runtimeProperties.alternateText) {
      alternateText = runtimeProperties.alternateText.endsWith(".")
        ? `${runtimeProperties.alternateText} `
        : `${runtimeProperties.alternateText}. `;
    }

    const selectGridRows: PaneRow[] = PaneDataStore.getPaneCollection(
      this.props.propagated.parentSelect.selectedDataId
    );

    if (selectGridRows.length === 1) {
      alternateText += Localization.getBuiltInMessage(
        "DataTable.selectCriteriaOneRowInTable"
      );
    } else if (selectGridRows.length > 1) {
      alternateText += Localization.getBuiltInMessage(
        "DataTable.selectCriteriaMultipleRowsInTable",
        { count: selectGridRows.length }
      );
    }

    return (
      <ApiButton
        alternateText={alternateText}
        buttonColor={this.props.buttonColor}
        dataId={this.props.dataId}
        disabledHelpText={this.props.disabledHelpText}
        iconName={this.props.iconName}
        isIconOnly={isIconOnly}
        label={label}
        name={this.props.name}
        onClick={this.onClick}
        size="small"
        tabIndex={-1}
      />
    );
  }
}

export default SelectButton;
